import './index.less';

import { useVuexState } from '@scfe-common/micro-frontend-utils';
import { AppMessage } from '@spx-portal/shared-components';
import { debounce } from 'lodash';
import * as React from 'react';
import { ProForm, ProTable } from 'react-pro-components';
import { ProFormProps } from 'react-pro-components/typings/components/pro-form/types';
import type { ProTableProps, ProTableRef } from 'react-pro-components/typings/components/pro-table/types';
import { Button, Form, Modal } from 'ssc-ui-react';

import { $gt, VueCtxStore } from '@/common/appCtx';
import useStationParams from '@/common/hooks/useStationParams';
import { isAdmin } from '@/utils/multiStation';
import { getSelectOptions } from '@/utils/safe-get';

import api from './api';
import DeviceSettingLog from './log';

interface AttendanceDeviceSettingModelType {
  station_id: string;
  device_name: string;
}

const EditDeviceSetting = (props: any) => {
  const curStationId = useVuexState('user.currentLoginUser.current_station_id');

  const {
    stationOptions,
    stationDeviceMap,
    loadAllStationDropdownList,
    showFormModal,
    curSetting,
    fetchData,
    onCancel,
    settingTable,
  } = props;
  const [form] = Form.useForm();

  const [curStation, setCurStation] = React.useState<number>();
  const [editDeviceOptions, setEditDeviceOptions] = React.useState<any[]>([]);

  const handleEditStationChange = (val: any) => {
    setCurStation(val);
    form.setFieldsValue({
      station_id: val,
      clock_in_devices: [],
      clock_out_devices: [],
    });
    const list = (stationDeviceMap[val] || []).map((device: string) => {
      return {
        label: device,
        value: device
      };
    });
    setEditDeviceOptions(list);
  };

  React.useEffect(() => {
    if (!isAdmin()) {
      form.setFieldValue('station_id', curStationId);
      handleEditStationChange(curStationId);
    }
    if (curSetting && curSetting.station_id) {
      setCurStation(curSetting.station_id);
      handleEditStationChange(curSetting.station_id);
      form.setFieldsValue({
        ...curSetting,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    await form.validateFields();
    const params = {
      ...form.getFieldsValue(),
    };
    try {
      if (curSetting && curSetting.station_id) {
        await api.updateDeviceSettings(params);
      } else {
        await api.createDeviceSettings(params);
      }
      settingTable.current.fetch();
      onCancel();
    } catch (error) {
      console.log(error);
    }
  };

  const proFormProps: ProFormProps = {
    formProps: {
      form,
      style: { width: '100%' },
      colon: false,
    },
    fields: [
      {
        type: 'select',
        name: 'station_id',
        label: $gt('Station'),
        required: true,
        rules: [
          { required: true, message: $gt('Please select Station') },
        ],
        ctrlProps: {
          allowClear: true,
          options: stationOptions,
          onSearch: debounce((value) => {
            const params: any = {
              count: value
                ? 100
                : 50,
            };
            if (value) params.station_name = value;
            loadAllStationDropdownList(params);
          }, 1000),
          disabled: !isAdmin(),
          onChange: (val: any) => handleEditStationChange(val),
        },
      },
      {
        type: 'select',
        name: 'clock_in_devices',
        label: $gt('Clock-in Device'),
        required: true,
        rules: [
          { required: true, message: $gt('Please select Clock-in Device') }
        ],
        ctrlProps: {
          allowClear: true,
          options: editDeviceOptions,
          showSearch: true,
          mode: 'multiple',
          maxTagCount: 2,
          disabled: curStation === undefined,
        },
      },
      {
        type: 'select',
        name: 'clock_out_devices',
        label: $gt('Clock-out Device'),
        required: true,
        rules: [
          { required: true, message: $gt('Please select Clock-out Device') }
        ],
        ctrlProps: {
          allowClear: true,
          options: editDeviceOptions,
          showSearch: true,
          mode: 'multiple',
          maxTagCount: 2,
          disabled: curStation === undefined,
        },
      },
    ],
    autoLabelWidth: true,
    footerProps: false,
  };

  return <>
    <Modal
      title="Attendance Device Setting"
      visible={showFormModal}
      width={600}
      onOk={onSubmit}
      onCancel={onCancel}
      getContainer={document.querySelector('.m-attendance-device-setting-container') as HTMLElement}
    >
      <ProForm style={{ overflow: 'hidden' }} {...proFormProps} />
    </Modal>
  </>;
};

const AttendanceDeviceSetting = () => {
  const [dataSource, setDataSource] = React.useState<any[]>([]);
  const [pagination, setPagination] = React.useState({ current: 1, pageSize: 10, total: 0 });

  const [stationOptions, setStationOptions] = React.useState<any[]>([]);
  const [allStationList, setAllStationList] = React.useState<any[]>([]);
  const [stationDeviceMap, setStationDeviceMap] = React.useState<any>({});
  const [deviceOptions, setDeviceOptions] = React.useState<any[]>([]);
  const curStationId = useVuexState('user.currentLoginUser.current_station_id');
  const { getStationDropdownParams } = useStationParams();

  const [showFormModal, setShowFormModal] = React.useState<boolean>(false);
  const [curSetting, setSetting] = React.useState<Object>({});

  const [showLogModal, setLogModal] = React.useState<boolean>(false);

  const settingTable = React.useRef<ProTableRef>();

  const fetchData = async(info: any) => {
    const { data = {} } = await api.getDeviceSettingsList({
      ...info?.form,
      station_id: isAdmin() ? info?.form?.station_id : curStationId,
      pageno: info.pagination?.current || 1,
      count: info.pagination?.pageSize || 20,
    });
    const { list = [], pageno = 1, count = 20, total = 0 } = data;
    setDataSource(list.map((item: any) => {
      return {
        ...item,
        clock_in_devices_str: (item.clock_in_devices || []).join(',') || '-',
        clock_out_devices_str: (item.clock_out_devices || []).join(',') || '-',
      };
    }));
    setPagination({ current: pageno, pageSize: count, total });
  };

  const updateBreadcrumb = () => {
    const breadcrumb = [
      { link: undefined, title: $gt('Workforce Management') },
      { link: '', title: $gt('Attendance Device Setting') },
    ];
    VueCtxStore.dispatch('sharedBreadcrumb/updateBreadcrumb', breadcrumb);
  };

  const loadAllStationDropdownList = async (params: any) => {
    try {
      if (!params.count) params.count = 50;
      const { list = [] } = await VueCtxStore.dispatch(
        'loadStationDropdownList',
        {
          ...params,
          ...getStationDropdownParams(),
        }
      );
      setStationOptions(getSelectOptions(list, {
        labelKey: 'station_name',
        valueKey: 'id',
      }));
    } catch (e) {
      console.error('load station dropdown list error: ', e);
    }
  };

  const handleStationChange = (val: any) => {
    const list = (stationDeviceMap[val] || []).map((device: string) => {
      return {
        label: device,
        value: device
      };
    });
    setDeviceOptions(list);
  };

  const loadAllDeviceList = async () => {
    try {
      const res = await api.getAllDeviceList({});
      setStationDeviceMap(res.data || {});
      const allList = [];
      for (let key in (res.data || {})) {
        allList.push(...res.data[key]);
      }
      const allDeviceListOptions = Array.from(new Set(allList)).map((device) => {
        return {
          label: device,
          value: device
        };
      });
      // handleStationChange(curStationId);
      const list = (res.data[curStationId] || []).map((device: string) => {
        return {
          label: device,
          value: device
        };
      });
      setAllStationList(isAdmin() ? allDeviceListOptions : list);
      setDeviceOptions(list);
    } catch (e) {
      console.error('load all device dropdown list error: ', e);
    }
  };

  const handleEditCancel = () => {
    setSetting({});
    setShowFormModal(false);
  };

  React.useEffect(() => {
    try {
      updateBreadcrumb();
      loadAllStationDropdownList({
        station_name: '',
        count: 99999,
      });
      loadAllDeviceList();
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const proTableProps: ProTableProps<AttendanceDeviceSettingModelType, any> = {
    searchForm: {
      fields: [
        {
          name: 'station_id',
          label: $gt('Station'),
          type: 'select',
          ctrlProps: {
            disabled: !isAdmin(),
            value: !isAdmin() ? curStationId : undefined,
            options: stationOptions,
            onSearch: debounce((value) => {
              const params: any = {
                count: value
                  ? 100
                  : 50,
              };
              if (value) params.station_name = value;
              loadAllStationDropdownList(params);
            }, 500),
            onSelect: (val: any) => handleStationChange(val),
          },
        },
        {
          name: 'device_name',
          label: $gt('Device Name'),
          type: 'select',
          ctrlProps: {
            options: allStationList,
            showSearch: true,
          },
        },
      ],
      footerProps: { showReset: true },
      columns: 3,
    },
    operation: (
      <>
        <Button
          type="primary"
          disabled={!isAdmin() && dataSource.length > 0}
          onClick={() => {
            isAdmin() && loadAllStationDropdownList({
              station_name: '',
              count: 100,
            });
            setShowFormModal(true);
          }}
        >
          { $gt('Create') }
        </Button>
        <Button onClick={() => setLogModal(true)}>
          { $gt('Log') }
        </Button>
      </>
    ),
    table: {
      dataSource,
      columns: [
        {
          title: $gt('Station'),
          dataIndex: 'station_name',
          width: 100,
          ellipsis: true,
          lineClamp: 1,
        },
        {
          title: $gt('Clock-in Device'),
          dataIndex: 'clock_in_devices_str',
          width: 300,
          ellipsis: true,
          lineClamp: 1,
        },
        {
          title: $gt('Clock-out Device'),
          dataIndex: 'clock_out_devices_str',
          width: 300,
          ellipsis: true,
          lineClamp: 1,
        },
      ],
      actionColumn: {
        actions: row => [
          {
            children: $gt('Edit'), onClick: () => {
              loadAllStationDropdownList({
                station_name: row.station_name,
                count: 100,
              });
              setSetting(row);
              setShowFormModal(true);
            }
          },
          {
            children: $gt('Delete'),
            onClick: async () => {
              try {
                const deviceList = [...row.clock_in_devices, ...row.clock_out_devices];
                await Modal.confirm({
                  title: 'Confirm',
                  content: (
                    <span style={{ wordBreak: 'break-word' }}>
                      {`Device's ID ${deviceList.slice(0, 5).join(', ')}${deviceList.length > 5 ? '...' : ''} configuration from station ${row.station_name} will be deleted`}
                    </span>
                  ),
                  onOk: async () => {
                    try {
                      await api.deleteDeviceSettings({
                        station_id: row.station_id,
                      });
                    } catch (error: any) {
                      AppMessage.error(992600062, error.message);
                      throw new Error(error.message);
                    }
                    settingTable.current?.fetch();
                  }
                });
              } catch (e) {
                console.log(e);
              }
            }
          },
        ],
        width: 100,
      },
      scroll: {
        x: 1200,
      },
      pagination,
      rowKey: 'station_id',
    },
    fetchData,
  };
  return (
    <div className="m-attendance-device-setting-container">
      <ProTable ref={settingTable} {...proTableProps} />
      {
        showFormModal
        ? <EditDeviceSetting
          stationOptions={stationOptions}
          stationDeviceMap={stationDeviceMap}
          loadAllStationDropdownList={loadAllStationDropdownList}
          showFormModal={showFormModal}
          curSetting={curSetting}
          fetchData={fetchData}
          onCancel={handleEditCancel}
          settingTable={settingTable}
        />
        : null
      }
      {
        showLogModal
        ? <DeviceSettingLog
          showLogModal={showLogModal}
          setLogModal={setLogModal}
          settingTable={settingTable}
        />
        : null
      }
    </div>
  );
};

export default AttendanceDeviceSetting;
